import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Alert, Box, Button, CircularProgress } from '@mui/material';
import PaymentOutlinedIcon from '@mui/icons-material/PaymentOutlined';
import { fetchSubscriptionStatus } from '../services/userSubscriptionApi';
import {
  isContractCheckoutPaymentContext,
  PAYMENT_FLOW_MANDATE_SETUP,
  PAYMENT_FLOW_CONTRACT_CHECKOUT,
} from '../utils/clientAppRedirect';
import { USER_CONTRACT_CHECKOUT_PATH } from './paths';

/**
 * Prompts subscription users to open contract checkout when admin contract is awaiting payment.
 */
export default function SubscriptionPaymentBanner({ sx }) {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [paymentContext, setPaymentContext] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetchSubscriptionStatus();
      setPaymentContext(res.data?.paymentContext ?? null);
    } catch {
      setPaymentContext(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 1, ...sx }}>
        <CircularProgress size={22} />
      </Box>
    );
  }

  if (!paymentContext || !isContractCheckoutPaymentContext(paymentContext)) {
    return null;
  }

  if (paymentContext.openContract?.paymentChannel === 'offline') {
    return (
      <Alert severity="info" sx={{ mb: 3, borderRadius: 2, ...sx }}>
        Contract {paymentContext.openContract.contractNumber || ''} is set for{' '}
        <strong>offline payment</strong>. Contact your administrator to complete payment and
        activation.
      </Alert>
    );
  }

  const contractNumber = paymentContext.openContract?.contractNumber;
  const billingSummary =
    paymentContext.billingSummary || paymentContext.openContract?.billingSummary;
  const amount =
    billingSummary?.totalAmount ?? paymentContext.openContract?.totalAmount ?? paymentContext.openContract?.amount;
  const currency = paymentContext.openContract?.currency || 'INR';
  const amountLabel =
    amount != null
      ? new Intl.NumberFormat('en-IN', {
          style: 'currency',
          currency,
          maximumFractionDigits: 0,
        }).format(Number(amount))
      : null;

  const title =
    paymentContext.paymentFlow === PAYMENT_FLOW_MANDATE_SETUP
      ? 'Complete autopay setup'
      : 'Contract payment required';

  return (
    <Alert
      severity="warning"
      sx={{ mb: 3, borderRadius: 2, alignItems: 'flex-start', ...sx }}
      action={
        <Button
          color="inherit"
          size="small"
          variant="outlined"
          startIcon={<PaymentOutlinedIcon />}
          onClick={() => navigate(USER_CONTRACT_CHECKOUT_PATH)}
          sx={{ fontWeight: 600, whiteSpace: 'nowrap', mt: { xs: 1, sm: 0 } }}
        >
          Pay now
        </Button>
      }
    >
      <strong>{title}</strong>
      {contractNumber ? ` — Contract ${contractNumber}` : ''}
      {amountLabel ? ` · ${amountLabel} due` : ''}
      <br />
      {paymentContext.message ||
        (paymentContext.paymentFlow === PAYMENT_FLOW_CONTRACT_CHECKOUT
          ? 'Your contract is ready. Pay online to activate your subscription and robot access.'
          : 'Open checkout to complete the next step.')}
    </Alert>
  );
}
